import React, { FunctionComponent, useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Container } from '@material-ui/core';
import { path } from 'app/utils/path';
import { _t } from 'app/utils/i18n';

import styles from './SideBar.module.scss';
import {
  DownArrowIcon,
  LockIcon,
  SearchIcon,
  UserImg,
} from 'app/assets/images/icons';
import { getModuleName } from 'influencers/utils/getModuleName';

import { useQueryClient } from 'react-query';
import { useAuth } from 'app/context/auth';
import { useModal } from 'app/context/Modal';
import { useAppSettings } from 'app/context/settings/appSettings';
import { checkTranslated } from 'app/utils/helpers';
import { getSearchState } from 'app/utils/search-state';

const SideBar: FunctionComponent = () => {
  const { user } = useAuth();
  const modal = useModal();
  const queryClient = useQueryClient();
  const { theme } = useAppSettings();
  const sideMenuState = theme.app.navigations;
  const modules: string[] =
    queryClient.getQueryData('getAuthorizedModules') || [];
  const location = useLocation();
  const activeTabIdFromSeach = getSearchState()?.activeTabId;
  const [activeTabId, setActiveTabId] = useState('');
  const [subMenuActiveTab, setSubMenuActiveTab] = useState('');
  const [inputValue, setInputValue] = useState('');
  const toggleParent = (id: any) => {
    if (activeTabId == id) {
      setActiveTabId('');
      setSubMenuActiveTab('');
    } else {
      setActiveTabId(id);
    }
  };
  const toggleSubMenu = (subMenu: string) => {
    if (subMenuActiveTab == subMenu) {
      setSubMenuActiveTab('');
    } else {
      setSubMenuActiveTab(subMenu);
    }
  };
  useEffect(() => {
    if (activeTabIdFromSeach) {
      setActiveTabId(activeTabIdFromSeach);
    }
  }, [activeTabIdFromSeach]);
  useEffect(() => {
    const path = location.pathname;
    const currentModule = getModuleName(path);
    sideMenuState.forEach((element: any) => {
      const id = element.id;
      element.menuItems?.forEach((item: any) => {
        if (path?.split('/')[1] != 'dashboard')
          if (
            item.link?.split('/')[1] == path?.split('/')[1] ||
            element.title == currentModule
          ) {
            setActiveTabId(id);
          }
        if (['all', 'dashboard', 'setting'].includes(path?.split('/')[2])) {
          toggleSubMenu(item.id);
        }
        item.subMenu?.forEach((sub: any) => {
          if (sub.link == path || path.includes(sub.link)) {
            setSubMenuActiveTab(item.id);
            setActiveTabId(id);
          }
        });
      });
    });
  }, [path, modules, location]);
  const isAuthorized = (module = '') =>
    [...modules, 'DASHBOARD', 'SETTINGS']?.includes(module);

  const openModal = () => {
    modal.show({
      isAlternative: true,
      message: _t(`basic:un_authorized`),
    });
  };
  const checkIfDimmed = (itemId: string) => {
    let isDimmed;

    switch (itemId) {
      case 'reportvideo':
        isDimmed = true;
        break;
      default:
        isDimmed = false;
    }
    return isDimmed;
  };
  return (
    <aside className={styles.sidebar}>
      <Container>
        <ul className={`sideBar ${styles.mainMenu}`}>
          {/* USER FIELD */}
          <li className={styles.searchField} id="sidebar_searchField">
            <input
              onChange={(e) => setInputValue(e.target.value)}
              value={inputValue}
              placeholder={_t('basic:search_menu')}
            />
            <SearchIcon className={styles.searchIcon} />
          </li>
          <li className={styles.userField} id="sidebar_userImgName">
            <Link to={path.users.USER_PROFILE_URI}>
              {user?.image_url ? (
                <div className={styles.userImg}>
                  <img alt="user" src={user?.image_url} />
                </div>
              ) : (
                <UserImg />
              )}
            </Link>
            <Link to={path.users.USER_PROFILE_URI}>
              <p>{user?.name}</p>
            </Link>
          </li>
          {/* sideMenuState  */}
          {/* SIDEBAR TABS */}
          {sideMenuState
            .filter((item: any) =>
              checkTranslated(
                item?.newTitle ? item?.newTitle.en : item?.title?.en,
                item?.newTitle ? item?.newTitle.ar : item?.title?.ar,
              )
                ?.toLowerCase()
                ?.includes(inputValue.toLowerCase()),
            )
            .map(
              (element: any) =>
                isAuthorized(element?.module) && (
                  <li
                    id={element.id}
                    key={element.id}
                    className={`${element.id === activeTabId ? 'active' : ''}`}
                  >
                    <p
                      className={`parent ${styles.listItem} `}
                      onClick={() =>
                        !isAuthorized(element?.module)
                          ? openModal()
                          : toggleParent(element.id)
                      }
                    >
                      {element.menuItems?.length == 0 ? (
                        <NavLink
                          to={checkIfDimmed(element.id) ? '#' : element?.link}
                          style={{ display: 'flex', alignItems: 'center' }}
                        >
                          {element?.icon}
                          <span>
                            {element?.newTitle
                              ? checkTranslated(
                                  element?.newTitle.en,
                                  element?.newTitle.ar,
                                )
                              : checkTranslated(
                                  element?.title?.en,
                                  element?.title.ar,
                                )}
                          </span>
                        </NavLink>
                      ) : (
                        <>
                          {element.icon}
                          {element?.newTitle
                            ? checkTranslated(
                                element?.newTitle.en,
                                element?.newTitle.ar,
                              )
                            : checkTranslated(
                                element?.title?.en,
                                element?.title?.ar,
                              )}
                          {!isAuthorized(element?.module) && <LockIcon />}
                          {/* <img
                              src={arrow}
                              alt="arrow_icon"
                              onClick={() => toggleParent(element.id)}
                            /> */}
                          <DownArrowIcon
                            onClick={() => toggleParent(element.id)}
                          />
                        </>
                      )}
                    </p>
                    {element?.menuItems?.length != 0 && (
                      <ul className={`${styles.subList}`}>
                        {element.menuItems.map(
                          (menuItem: any, index: number) =>
                            menuItem.subMenu ? (
                              <li
                                key={`${menuItem.id}-${index}`}
                                className={`${
                                  menuItem.id == subMenuActiveTab
                                    ? 'active'
                                    : ''
                                }`}
                                onClick={() => toggleSubMenu(menuItem.id)}
                              >
                                <p>
                                  {menuItem?.newTitle
                                    ? checkTranslated(
                                        menuItem?.newTitle.en,
                                        menuItem?.newTitle.ar,
                                      )
                                    : checkTranslated(
                                        menuItem?.item?.en,
                                        menuItem?.item?.ar,
                                      )}
                                  <DownArrowIcon />
                                </p>
                                <ul className={styles.smallList}>
                                  {menuItem.subMenu.map(
                                    (subItem: any, index: number) => (
                                      <li
                                        key={`${subItem.id}-${index}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                        }} // to prevent closing submenu on sublink click
                                      >
                                        <NavLink to={subItem.link}>
                                          {subItem?.newTitle
                                            ? checkTranslated(
                                                subItem?.newTitle?.en,
                                                subItem?.newTitle?.ar,
                                              )
                                            : checkTranslated(
                                                subItem?.item?.en,
                                                subItem?.item?.ar,
                                              )}
                                        </NavLink>
                                      </li>
                                    ),
                                  )}
                                </ul>
                              </li>
                            ) : (
                              <li
                                key={`${menuItem.id}-${index}`}
                                onClick={() => toggleSubMenu(menuItem.item)}
                              >
                                <p>
                                  {checkIfDimmed(menuItem.id) ? (
                                    <span
                                      style={{
                                        cursor: 'default',
                                        color: 'gray',
                                      }}
                                    >
                                      {menuItem?.newTitle
                                        ? checkTranslated(
                                            menuItem?.newTitle?.en,
                                            menuItem?.newTitle?.ar,
                                          )
                                        : checkTranslated(
                                            menuItem?.item?.en,
                                            menuItem?.item?.ar,
                                          )}
                                    </span>
                                  ) : (
                                    <NavLink to={menuItem?.link}>
                                      {menuItem?.newTitle
                                        ? checkTranslated(
                                            menuItem?.newTitle?.en,
                                            menuItem?.newTitle?.ar,
                                          )
                                        : checkTranslated(
                                            menuItem?.item?.en,
                                            menuItem?.item?.ar,
                                          )}
                                    </NavLink>
                                  )}
                                </p>
                              </li>
                            ),
                        )}
                      </ul>
                    )}
                  </li>
                ),
            )}
        </ul>
      </Container>
    </aside>
  );
};

export default SideBar;
